import { DomainError } from '../errors.js';

/** Durable job lifecycle (ADR-0007). */
export const JOB_STATES = ['queued', 'running', 'retrying', 'completed', 'failed', 'cancelled'] as const;
export type JobState = (typeof JOB_STATES)[number];

const TRANSITIONS: Record<JobState, readonly JobState[]> = {
  queued: ['running', 'cancelled'],
  // `retrying` rather than straight back to `queued`: the distinction is what makes
  // a job that keeps failing visible on the dashboard instead of looking fresh.
  running: ['completed', 'failed', 'retrying', 'cancelled'],
  retrying: ['running', 'failed', 'cancelled'],
  completed: [],
  failed: [],
  cancelled: [],
};

export const TERMINAL_JOB_STATES: readonly JobState[] = ['completed', 'failed', 'cancelled'];

export function isTerminalJobState(state: JobState): boolean {
  return TERMINAL_JOB_STATES.includes(state);
}

export function canTransitionJob(from: JobState, to: JobState): boolean {
  return TRANSITIONS[from].includes(to);
}

export function assertJobTransition(from: JobState, to: JobState): void {
  if (!canTransitionJob(from, to)) {
    throw new DomainError('INVALID_STATE_TRANSITION', `Job cannot move from ${from} to ${to}`, {
      from,
      to,
      allowed: TRANSITIONS[from],
    });
  }
}

/**
 * Only a job that has not started can be cancelled outright. Once it is running,
 * cancellation is a request the handler has to honour and compensate for, which
 * is why `cancelled` is reachable from `running` but a caller cannot simply set it.
 */
export function isCancellable(state: JobState): boolean {
  return state === 'queued' || state === 'retrying';
}
